import React from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  Calendar, 
  Camera, 
  FileText, 
  Shield, 
  Clock,
  Phone,
  MapPin,
  Star
} from 'lucide-react';

const BookInspectionPage = () => {
  return (
    <Layout>
      <Helmet>
        <title>Book Free Roof Inspection | Carolina Trophy Roofs | Eastern NC</title>
        <meta name="description" content="Schedule your free roof inspection with Carolina Trophy Roofs. Professional assessments, storm damage evaluation, and detailed reports. Serving Rocky Mount, Greenville, Wilson, and Eastern NC." />
        <meta name="keywords" content="free roof inspection, roof assessment, storm damage inspection, Rocky Mount roofing, Greenville roofing, Eastern NC roofers" />
        <link rel="canonical" href="https://carolinatrophyroofs.com/book-inspection" />
        
        <meta property="og:title" content="Book Free Roof Inspection | Carolina Trophy Roofs" />
        <meta property="og:description" content="Professional roof inspections and storm damage assessments. Free estimates and detailed reports." />
        <meta property="og:url" content="https://carolinatrophyroofs.com/book-inspection" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-brand-charcoal-gray to-gray-800 text-white py-20 md:py-28">
        <div className="absolute inset-0">
          <img 
            src="/lovable-uploads/47e27f09-bb73-4372-b882-b62c8f6a0e64.png" 
            alt="Professional roof inspection with Carolina Trophy Roofs team" 
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative container mx-auto px-4 z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 bg-brand-safety-orange text-white px-4 py-2 text-sm font-semibold">
              100% FREE INSPECTION
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-brand-trophy-gold">
              Schedule Your Free Roof Inspection
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white max-w-3xl mx-auto">
              Professional roof assessments, storm damage evaluation, and detailed reports from Eastern NC's trusted roofing experts
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://carolinatrophyroofs.sky-quote.com/schedule#step1" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold px-8 py-4">
                  <Calendar className="mr-2 h-5 w-5" />
                  Schedule Inspection
                </Button>
              </a>
              <Button size="lg" variant="outline" className="border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold/10">
                <Phone className="mr-2 h-5 w-5" />
                Call (252) 302-2003
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          
          {/* What's Included Section */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-4 text-brand-charcoal-gray">
              What's Included in Your Free Inspection & Consultation
            </h2>
            <p className="text-xl text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Our free inspection includes a thorough assessment and consultation about your roof's condition
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: CheckCircle,
                  title: "Visual Roof Inspection",
                  description: "Thorough visual assessment of your roof's overall condition, identifying any obvious issues or concerns."
                },
                {
                  icon: Shield,
                  title: "Storm Damage Evaluation",
                  description: "Professional evaluation for hail damage, wind damage, and weather-related roofing issues."
                },
                {
                  icon: FileText,
                  title: "Expert Consultation",
                  description: "On-site discussion of findings, recommended next steps, and answers to all your roofing questions."
                },
                {
                  icon: Star,
                  title: "Insurance Claim Guidance",
                  description: "Initial guidance on insurance claims process when storm damage is identified."
                },
                {
                  icon: Camera,
                  title: "Detailed Reports Available*",
                  description: "Comprehensive photo documentation, measurements, and written reports available as a paid service."
                },
                {
                  icon: Clock,
                  title: "Lifespan Assessment Available*",
                  description: "Professional evaluation of remaining roof life and maintenance schedules available as a paid service."
                }
              ].map((service, index) => (
                <Card key={index} className="border-2 border-gray-200 hover:border-brand-trophy-gold/30 transition-all duration-300 hover:shadow-lg">
                  <CardHeader className="text-center pb-4">
                    <div className="w-16 h-16 bg-brand-trophy-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <service.icon className="h-8 w-8 text-brand-trophy-gold" />
                    </div>
                    <CardTitle className="text-xl font-bold text-brand-charcoal-gray">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-center">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Process Section */}
          <div className="mb-16 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 md:p-12">
            <h2 className="text-4xl font-bold text-center mb-12 text-brand-charcoal-gray">
              Our Inspection Process
            </h2>
            
            <div className="grid md:grid-cols-4 gap-8">
              {[
                {
                  step: "1",
                  title: "Schedule Online",
                  description: "Book your free inspection at your convenience through our easy online scheduling system."
                },
                {
                  step: "2", 
                  title: "Professional Visit",
                  description: "Our certified roofing experts arrive on time with all necessary equipment and safety gear."
                },
                {
                  step: "3",
                  title: "Thorough Inspection",
                  description: "Complete roof assessment including photos, measurements, and detailed condition evaluation."
                },
                {
                  step: "4",
                  title: "Expert Consultation",
                  description: "Receive on-site consultation with findings and recommendations. Detailed reports available as paid service."
                }
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-brand-safety-orange rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-lg">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-brand-charcoal-gray">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Service Areas */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-4 text-brand-charcoal-gray">
              Service Areas
            </h2>
            <p className="text-xl text-center text-gray-600 mb-8">
              Free roof inspections throughout Eastern North Carolina
            </p>
            
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4 text-center">
              {[
                "Rocky Mount", "Greenville", "Wilson", "Tarboro", 
                "Zebulon", "Raleigh", "Wake Forest", "Youngsville",
                "Spring Hope", "Louisburg", "Franklinton", "Garner"
              ].map((city, index) => (
                <div key={index} className="flex items-center justify-center p-3 bg-gray-50 rounded-lg">
                  <MapPin className="h-4 w-4 text-brand-safety-orange mr-2" />
                  <span className="font-medium text-brand-charcoal-gray">{city}, NC</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <Card className="bg-gradient-to-r from-brand-charcoal-gray to-gray-800 text-white">
            <CardContent className="p-8 md:p-12 text-center">
              <h2 className="text-3xl font-bold mb-4 text-brand-trophy-gold">
                Ready to Schedule Your Free Inspection?
              </h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-200">
                Don't wait for small problems to become big expenses. Get your free consultation today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="https://carolinatrophyroofs.sky-quote.com/schedule#step1" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold px-8 py-4">
                    <Calendar className="mr-2 h-5 w-5" />
                    Schedule Now
                  </Button>
                </a>
                <Button size="lg" variant="outline" className="border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold/10">
                  <Phone className="mr-2 h-5 w-5" />
                  Call (252) 302-2003
                </Button>
              </div>
              <div className="mt-8 text-sm text-gray-300">
                <p>✓ No obligation • ✓ Fully Insured • ✓ Same-day response available</p>
                <p className="mt-2">*Detailed reports and assessments available as paid services</p>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </Layout>
  );
};

export default BookInspectionPage;